// src/lib/measurement.ts
/**
 * Measurement utilities for T-16: Measurement & Distance Tool
 * 
 * Provides functions for calculating and formatting measurements
 * between two points on the number line.
 */

import { Fraction } from '../types/redux';
import { formatFraction } from './fractions';

export interface MeasurementResult {
  delta: number;
  deltaFraction?: Fraction;
  formattedValue: string;
  isExactFraction: boolean;
  isSnapped?: boolean;
  snappedStartValue?: number;
  snappedEndValue?: number;
}

/**
 * Calculate measurement between two values
 * Returns both decimal and fraction representations
 */
export function calculateMeasurement(
  startValue: number,
  endValue: number,
  maxDenominator: number = 16,
  isSnapped: boolean = false,
  snappedStartValue?: number,
  snappedEndValue?: number
): MeasurementResult {
  // If snapped, use the snapped tick values for calculation
  const actualStartValue = isSnapped && snappedStartValue !== undefined ? snappedStartValue : startValue;
  const actualEndValue = isSnapped && snappedEndValue !== undefined ? snappedEndValue : endValue;
  
  const delta = Math.abs(actualEndValue - actualStartValue);
  
  // Try to represent as exact fraction
  const fraction = tryExactFraction(delta, maxDenominator);
  
  if (fraction) {
    return {
      delta,
      deltaFraction: fraction,
      formattedValue: formatFraction(fraction),
      isExactFraction: true,
      isSnapped,
      snappedStartValue: actualStartValue,
      snappedEndValue: actualEndValue,
    };
  }
  
  // Fall back to decimal with reasonable precision
  return {
    delta,
    formattedValue: delta.toFixed(3).replace(/\.?0+$/, ''),
    isExactFraction: false,
    isSnapped,
    snappedStartValue: actualStartValue,
    snappedEndValue: actualEndValue,
  };
}

/**
 * Try to represent decimal as exact fraction with limited denominator
 */
function tryExactFraction(decimal: number, maxDen: number): Fraction | null {
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(decimal * d);
    if (Math.abs(n / d - decimal) < 1e-10) {
      return { n, d };
    }
  }
  return null;
}

/**
 * Format measurement result for display
 * Shows delta symbol and value
 */
export function formatMeasurementDisplay(result: MeasurementResult): string {
  return `Δ = ${result.formattedValue}`;
}

/**
 * Check if a measurement is valid (has both start and end points)
 */
export function isValidMeasurement(
  startPoint?: { x: number; value: number },
  endPoint?: { x: number; value: number }
): boolean {
  return startPoint !== undefined && endPoint !== undefined;
} 